import { Typography, Card, Box, Breadcrumbs, Button, Alert, CircularProgress } from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  UserCog,
  Mail,
  Phone,
  Building2,
  MapPin,
  Clock,
  Edit,
  Trash2,
  Power,
  ArrowLeft,
  ShieldCheck,
  Activity,
} from 'lucide-react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'

import { MODULE_NAME, PERMISSIONS } from '../../../constants'
import { hasPermission } from '../../../utils'
import { deleteUser, extractItem, getUserByUuid, normalizeUser, updateUserStatus } from '../apis'
import { userStatusConfig } from '../constant'

import { InfoRow, SectionHeader, StatusBadge } from '@/common/components'
import { formatLongDate, useTimezone } from '@/utils/datetime'

const PLACEHOLDER = 'N/A'

function displayValue(value) {
  if (value === null || value === undefined) return PLACEHOLDER
  if (typeof value === 'string' && value.trim() === '') return PLACEHOLDER
  return value
}

function swalDefaults() {
  return {
    confirmButtonColor: '#3B82F6',
    cancelButtonColor: '#151f3b',
    background: 'var(--card-bg, #ffffff)',
    color: 'var(--text-primary, #212529)',
    customClass: {
      confirmButton: 'swal-btn-primary',
      cancelButton: 'swal-btn-secondary',
    },
    didOpen: () => {
      const container = document.querySelector('.swal2-container')
      if (container) container.style.zIndex = '2000'
    },
  }
}

function formatDate(value) {
  return formatLongDate(value, PLACEHOLDER)
}

export default function UserDetail() {
  // Re-render this page when the admin changes the Global Settings
  // timezone, so every timestamp below re-formats immediately.
  useTimezone()
  const { uuid } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Branch-scoped permissions — hide update/delete controls the current
  // user is not allowed to use.
  const canUpdate = hasPermission(MODULE_NAME.USER_MANAGEMENT, PERMISSIONS.UPDATE)
  const canDelete = hasPermission(MODULE_NAME.USER_MANAGEMENT, PERMISSIONS.DELETE)

  const userQuery = useQuery({
    queryKey: ['guardian-user', uuid],
    queryFn: () => getUserByUuid(uuid),
    enabled: Boolean(uuid),
  })

  const statusMutation = useMutation({
    mutationFn: ({ nextStatus, roleUuid }) => updateUserStatus(uuid, nextStatus, roleUuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guardian-user', uuid] })
      queryClient.invalidateQueries({ queryKey: ['guardian-users'] })
      queryClient.invalidateQueries({ queryKey: ['guardian-users-count'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => deleteUser(uuid),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['guardian-users'] })
      await Swal.fire({
        ...swalDefaults(),
        title: 'User deleted',
        icon: 'success',
        iconColor: '#198754',
        confirmButtonText: 'OK',
      })
      navigate('/user-management')
    },
  })

  const rawUser = extractItem(userQuery.data)
  const user = rawUser ? normalizeUser(rawUser) : null

  const isLoading = userQuery.isLoading || userQuery.isFetching
  const isError = userQuery.isError
  const errorMessage =
    userQuery.error?.response?.data?.status?.message ||
    userQuery.error?.response?.data?.message ||
    userQuery.error?.message ||
    'Failed to load user details.'

  const fullName =
    `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || user?.email || 'User'
  const statusKey = user?.status || 'INACTIVE'
  const statusMeta = userStatusConfig[statusKey] || { label: statusKey }

  const handleToggleStatus = async () => {
    if (!user) return
    const nextStatus = statusKey === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'
    const result = await Swal.fire({
      ...swalDefaults(),
      title: `${statusKey === 'ACTIVE' ? 'Deactivate' : 'Activate'} ${fullName}?`,
      text: `Status will be changed to ${nextStatus} immediately.`,
      icon: 'warning',
      iconColor: '#f7b84b',
      showCancelButton: true,
      confirmButtonText: statusKey === 'ACTIVE' ? 'Deactivate' : 'Activate',
      cancelButtonText: 'Cancel',
    })
    if (result.isConfirmed) {
      try {
        await statusMutation.mutateAsync({ nextStatus, roleUuid: user.roleUuid })
        await Swal.fire({
          ...swalDefaults(),
          title: 'Status updated',
          icon: 'success',
          iconColor: '#198754',
          confirmButtonText: 'OK',
        })
      } catch (err) {
        await Swal.fire({
          ...swalDefaults(),
          title: 'Failed to update status',
          text:
            err?.response?.data?.status?.message ||
            err?.response?.data?.message ||
            err?.message ||
            'Please try again.',
          icon: 'error',
          confirmButtonText: 'OK',
        })
      }
    }
  }

  const handleDelete = async () => {
    if (!user) return
    const result = await Swal.fire({
      ...swalDefaults(),
      title: `Delete ${fullName}?`,
      text: 'This will permanently remove this user and revoke their access. This action cannot be undone.',
      icon: 'warning',
      iconColor: '#DC3545',
      showCancelButton: true,
      confirmButtonText: 'Delete user',
      cancelButtonText: 'Cancel',
    })
    if (result.isConfirmed) {
      try {
        await deleteMutation.mutateAsync()
      } catch (err) {
        await Swal.fire({
          ...swalDefaults(),
          title: 'Failed to delete user',
          text:
            err?.response?.data?.status?.message ||
            err?.response?.data?.message ||
            err?.message ||
            'Please try again.',
          icon: 'error',
          confirmButtonText: 'OK',
        })
      }
    }
  }

  if (isLoading && !user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  if (isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => userQuery.refetch()}>
              Retry
            </Button>
          }
        >
          {errorMessage}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowLeft size={14} />}
          onClick={() => navigate('/user-management')}
          sx={{ mt: 2 }}
        >
          Back to users
        </Button>
      </Box>
    )
  }

  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body1" color="text.secondary">
          User not found.
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowLeft size={14} />}
          onClick={() => navigate('/user-management')}
          sx={{ mt: 2 }}
        >
          Back to users
        </Button>
      </Box>
    )
  }

  const overviewItems = [
    {
      label: 'Role',
      value: displayValue(user.role),
      icon: <ShieldCheck size={13} />,
    },
    {
      label: 'Branch ID',
      value: user.branchId ?? PLACEHOLDER,
      icon: <Building2 size={13} />,
    },
    {
      label: 'Platform ID',
      value: user.platformId ?? PLACEHOLDER,
      icon: <Activity size={13} />,
    },
  ]

  const recentActivity = [
    {
      when: formatDate(user.createdAt),
      what: `Account created with status ${statusMeta.label}`,
    },
  ]

  return (
    <div className="sensor-page">
      <div className="sensor-topbar">
        <div>
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
            <Link to="/user-management" underline="hover" color="inherit">
              User Management
            </Link>
            <Typography variant="body2" color="text.primary">
              {fullName}
            </Typography>
          </Breadcrumbs>
          <div className="flex items-center gap-3 mb-1">
            <Typography variant="h5">{fullName}</Typography>
            <StatusBadge value={statusKey} config={userStatusConfig} dot>
              {statusMeta.label}
            </StatusBadge>
            {user.role && <StatusBadge color="blue">{user.role}</StatusBadge>}
          </div>
          <Typography className="text-gray-400">
            {user.email} · joined {formatDate(user.createdAt)}
          </Typography>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button
            variant="outlined"
            startIcon={<Edit size={14} />}
            onClick={() => navigate('/user-management')}
          >
            Back to list
          </Button>
          {canUpdate && (
            <Button
              variant="outlined"
              startIcon={<Power size={14} />}
              onClick={handleToggleStatus}
              disabled={statusMutation.isPending}
            >
              {statusKey === 'ACTIVE' ? 'Deactivate' : 'Activate'}
            </Button>
          )}
          {canDelete && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<Trash2 size={14} />}
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              Delete user
            </Button>
          )}
        </div>
      </div>

      <div className="sensor-body">
        <div className="flex flex-col gap-5">
          <Card className="px-4 py-3">
            <SectionHeader icon={Activity} label="Overview" />
            <div className="sensor-metrics">
              {overviewItems.map((m) => (
                <div key={m.label} className="sensor-metric">
                  <span className="sensor-metric__label flex gap-1 items-center">
                    {m.icon} {m.label}
                  </span>
                  <span className="sensor-metric__value">{m.value}</span>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-5">
            <SectionHeader icon={Activity} label="Recent Activity" />
            <Box className="flex flex-col gap-2" sx={{ mt: 1 }}>
              {recentActivity.map((row, i) => (
                <Box
                  key={i}
                  sx={{
                    p: 1.25,
                    border: '1px solid var(--divider-color)',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {row.when}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {row.what}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Card>
        </div>

        <div className="flex flex-col gap-5">
          <Card className="p-5">
            <SectionHeader icon={UserCog} label="Profile" />
            <InfoRow label="Email" icon={<Mail size={14} />} value={displayValue(user.email)} />
            <InfoRow
              label="Phone number"
              icon={<Phone size={14} />}
              value={displayValue(user.phoneNumber)}
            />
            <InfoRow
              label="Address"
              icon={<MapPin size={14} />}
              value={displayValue(user.address)}
            />
            <InfoRow label="Role" value={displayValue(user.role)} />
            <InfoRow
              label="Branch ID"
              icon={<MapPin size={14} />}
              value={user.branchId ?? PLACEHOLDER}
            />
            <InfoRow
              label="Platform ID"
              icon={<Building2 size={14} />}
              value={user.platformId ?? PLACEHOLDER}
            />
            <InfoRow label="Company ID" value={user.companyId ?? PLACEHOLDER} />
            <InfoRow label="UUID" icon={<Clock size={14} />} value={displayValue(user.uuid)} />
          </Card>

          <Card className="p-5">
            <SectionHeader icon={Building2} label="Status" />
            <InfoRow
              label="Account status"
              value={<StatusBadge value={statusKey} config={userStatusConfig} dot />}
            />
            <InfoRow label="Joined" value={formatDate(user.createdAt)} />
          </Card>

          <Card className="p-5">
            <Box className="flex flex-col gap-2">
              <Button
                variant="outlined"
                startIcon={<ArrowLeft size={14} />}
                onClick={() => navigate('/user-management')}
              >
                Back to users
              </Button>
            </Box>
          </Card>
        </div>
      </div>
    </div>
  )
}
